// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Importér RTAB-Map-sessioner (frontend-aev.1): pick several session `.db`
 * files and import them into this case one after the other — chunked
 * upload (`/imports/rtabmap`, ruxd PR #19) with per-session progress and
 * ETA, then the `import_rtabmap` job. Each session lands DETACHED where its
 * own map put it; a session already in the case is refused (409
 * `session_already_imported`) and skipped. Sessions go in file order, so
 * their ordinals (s0, s1, …) follow the list.
 *
 * Cancel abandons the upload in flight (DELETE) and the queue after it;
 * "Prøv igen" restarts a failed session (the chunk sender resumes from the
 * server's own byte count within an upload).
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { api, type RtabmapUploadHandle } from '../../api/client';
import type { Job } from '../../api/types';
import { isTerminal } from '../../api/types';
import { formatBytes } from '../../app/cases';
import { markOwnImport } from '../../app/bygning/ownImports';
import { useCanEdit } from '../../app/CaseRoleContext';
import { useJobs } from '../../app/JobsContext';
import { sendIfcChunks, uploadNoticeText } from '../../bygning/bimImport';
import {
  SQLITE_HEAD_BYTES,
  etaSeconds,
  formatEta,
  importJobSummary,
  isBusy,
  isFinished,
  overallFraction,
  rtabmapErrorText,
  rtabmapFileProblem,
  rtabmapJobErrorText,
  sessionFraction,
  type RtabmapStep,
  type SessionItem,
  type SessionState,
} from '../../posegraf/rtabmapImport';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { FormDialog } from '../kortlaegning/FormDialog';
import formStyles from '../kortlaegning/FormDialog.module.css';
import styles from './RtabmapImportDialog.module.css';

const MAX_NAME = 200;

export interface RtabmapImportDialogProps {
  onClose: () => void;
  /** Every session is through (some imported): the editor refetches and shows them. */
  onImported: () => void;
  /**
   * A panel of Rediger › Import (frontend-u0o.1) rather than a modal: no
   * Annullér on an idle form, "Ryd" / "Ny import" clears the list, and the
   * primary button after the last session runs `onShow` (open Scan & pose).
   */
  inline?: boolean;
  onShow?: () => void;
}

interface Picked {
  file: File;
  item: SessionItem;
}

export function RtabmapImportDialog({ onClose, onImported, inline = false, onShow }: RtabmapImportDialogProps) {
  // The page shows the way in to editors only; a viewer who gets here anyway cannot start.
  const canEdit = useCanEdit();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<Picked[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef<{ controller: AbortController; handle: RtabmapUploadHandle | null; completeSent: boolean } | null>(null);
  const { jobs: jobList } = useJobs();
  const jobs = useMemo(() => Object.fromEntries(jobList.map((j) => [j.id, j])) as Record<string, Job>, [jobList]);
  const jobsRef = useRef<Record<string, Job>>(jobs);
  jobsRef.current = jobs;
  const anyImported = useRef(false);

  // Leaving mid-upload abandons it (the server deletes the staged bytes).
  useEffect(
    () => () => {
      const f = inflight.current;
      if (!f) return;
      f.controller.abort();
      if (f.handle && !f.completeSent) void f.handle.abort().catch(() => undefined);
    },
    [],
  );

  const setState = (key: string, state: SessionState | ((s: SessionState) => SessionState)) =>
    setPicked((list) => list.map((p) => (p.item.key === key ? { ...p, item: { ...p.item, state: typeof state === 'function' ? state(p.item.state) : state } } : p)));

  async function add(files: FileList | File[] | null) {
    if (!files) return;
    const fresh: Picked[] = [];
    for (const [i, file] of [...files].entries()) {
      const head = new Uint8Array(await file.slice(0, SQLITE_HEAD_BYTES).arrayBuffer());
      const problem = rtabmapFileProblem(file.name, file.size, head);
      const key = `${file.name}:${file.size}:${Date.now()}:${i}`;
      fresh.push({ file, item: { key, name: file.name, size: file.size, state: problem ? { kind: 'invalid', reason: problem } : { kind: 'queued' } } });
    }
    setPicked((list) => [...list, ...fresh.filter((f) => !list.some((p) => p.file.name === f.file.name && p.file.size === f.file.size))]);
    setError(null);
  }

  /** Resolves with the job once it is finished (from the event stream; polled off a ref). */
  function waitForJob(id: string, signal: AbortSignal): Promise<Job> {
    return new Promise((resolve, reject) => {
      const timer = setInterval(() => {
        if (signal.aborted) {
          clearInterval(timer);
          reject(new DOMException('aborted', 'AbortError'));
          return;
        }
        const job = jobsRef.current[id];
        if (job && isTerminal(job.status)) {
          clearInterval(timer);
          resolve(job);
        }
      }, 250);
    });
  }

  async function importOne(p: Picked, controller: AbortController): Promise<'ok' | 'skipped' | 'failed' | 'aborted'> {
    const { file, item } = p;
    const current: { controller: AbortController; handle: RtabmapUploadHandle | null; completeSent: boolean } = { controller, handle: null, completeSent: false };
    inflight.current = current;
    let step: RtabmapStep = 'begin';
    const startedAt = Date.now();
    setState(item.key, { kind: 'uploading', sent: 0, total: file.size, startedAt, notice: null });
    try {
      const handle = await api.beginRtabmapUpload(file.name.slice(0, MAX_NAME), file.size, controller.signal);
      current.handle = handle;
      const problem = rtabmapFileProblem(file.name, file.size, null, handle.upload.max_bytes);
      if (problem) throw new RangeError(problem);
      step = 'chunk';
      await sendIfcChunks(
        file.size,
        handle.upload.chunk_bytes,
        handle.upload.received,
        {
          put: (start, end) => handle.append(start, file.slice(start, end), controller.signal),
          status: () => handle.status(controller.signal),
          sleep: (ms) => new Promise((done) => setTimeout(done, ms)),
        },
        (received) => setState(item.key, { kind: 'uploading', sent: received, total: file.size, startedAt, notice: null }),
        controller.signal,
        (notice) => setState(item.key, (s) => (s.kind === 'uploading' ? { ...s, notice: uploadNoticeText(notice, s.sent, s.total) } : s)),
      );
      step = 'complete';
      setState(item.key, { kind: 'starting' });
      current.completeSent = true;
      const job = await handle.complete({ append_session: true }, controller.signal);
      markOwnImport(job.id);
      setState(item.key, { kind: 'importing', jobId: job.id });
      const done = await waitForJob(job.id, controller.signal);
      inflight.current = null;
      if (done.status !== 'succeeded') {
        setState(item.key, { kind: 'failed', message: rtabmapJobErrorText(done) });
        return 'failed';
      }
      anyImported.current = true;
      setState(item.key, { kind: 'done', summary: importJobSummary(done) });
      return 'ok';
    } catch (cause) {
      if (controller.signal.aborted) return 'aborted';
      inflight.current = null;
      if (current.handle && !current.completeSent) void current.handle.abort().catch(() => undefined);
      const message = rtabmapErrorText(cause, step);
      const duplicate = cause instanceof Error && 'code' in cause && (cause as { code?: string }).code === 'session_already_imported';
      setState(item.key, duplicate ? { kind: 'duplicate', message } : { kind: 'failed', message });
      return duplicate ? 'skipped' : 'failed';
    }
  }

  async function run(only?: string) {
    if (!canEdit) return;
    const queue = picked.filter((p) => (only ? p.item.key === only : p.item.state.kind === 'queued'));
    if (queue.length === 0) {
      setError(picked.length === 0 ? 'Vælg en eller flere sessioner (.db) først.' : 'Der er ingen sessioner at importere.');
      return;
    }
    setError(null);
    setRunning(true);
    const controller = new AbortController();
    for (const p of queue) {
      if (controller.signal.aborted) break;
      const outcome = await importOne(p, controller);
      if (outcome === 'aborted') break;
    }
    setRunning(false);
  }

  function cancel() {
    const f = inflight.current;
    inflight.current = null;
    if (f) {
      f.controller.abort();
      if (f.handle && !f.completeSent) void f.handle.abort().catch(() => undefined);
    }
    setPicked((list) => list.map((p) => (isBusy(p.item.state) || p.item.state.kind === 'queued' ? { ...p, item: { ...p.item, state: { kind: 'cancelled' } } } : p)));
    setRunning(false);
    setError(f?.completeSent ? 'Afbrudt. Importen, der allerede var startet, kører videre på serveren.' : 'Importen blev annulleret. Uploads i gang er slettet.');
  }

  function close() {
    if (running) return;
    if (anyImported.current) onImported();
    if (!inline) {
      onClose();
      return;
    }
    // Inline: the panel stays; start over with an empty list.
    anyImported.current = false;
    setPicked([]);
    setError(null);
  }

  function show() {
    if (running) return;
    if (anyImported.current) onImported();
    if (inline) onShow?.();
    else onClose();
  }

  const items = picked.map((p) => p.item);
  const queued = items.filter((i) => i.state.kind === 'queued').length;
  const allThrough = items.length > 0 && items.every((i) => isFinished(i.state));
  const overall = overallFraction(items, jobs);
  const totalBytes = items.filter((i) => i.state.kind !== 'invalid').reduce((n, i) => n + i.size, 0);

  return (
    <FormDialog
      title={inline ? 'RTAB-Map-sessioner' : 'Importér RTAB-Map-sessioner'}
      subtitle="Hver session lægges i sagen frakoblet, dér hvor dens eget kort placerede den. Blokke og led justeres bagefter i Scan & pose."
      onCancel={inline ? () => undefined : close}
      onSubmit={() => (allThrough ? show() : void run())}
      error={error}
      className={inline ? undefined : styles.dialog}
      inline={inline}
      actions={
        running ? (
          <Button variant="outline" tone="danger" onClick={cancel}>
            Annullér import
          </Button>
        ) : (
          <>
            {(!inline || items.length > 0) && (
              <Button variant="outline" onClick={close}>
                {inline ? (allThrough ? 'Ny import' : 'Ryd') : allThrough ? 'Luk' : 'Annullér'}
              </Button>
            )}
            {allThrough ? (
              <Button type="submit" variant="primary">
                Vis i Scan &amp; pose
              </Button>
            ) : (
              <Button type="submit" variant="primary" disabled={queued === 0 || !canEdit}>
                {queued > 1 ? `Importér ${queued} sessioner` : 'Importér session'}
              </Button>
            )}
          </>
        )
      }
    >
      <div className={formStyles.field}>
        <span className={formStyles.fieldLabel}>Sessioner</span>
        <input
          id={inputId}
          ref={inputRef}
          type="file"
          accept=".db"
          multiple
          className={styles.fileInput}
          onChange={(e) => {
            void add(e.target.files);
            e.target.value = '';
          }}
        />
        <div
          className={styles.drop}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            void add(e.dataTransfer.files);
          }}
        >
          <span className={styles.help}>Træk session-databaser (.db) hertil, eller vælg dem. Højst 4 GB pr. session.</span>
          <Button variant="outline" size="sm" disabled={running} onClick={() => inputRef.current?.click()}>
            Vælg filer…
          </Button>
        </div>
      </div>

      {items.length > 0 && (
        <>
          <div className={styles.overall}>
            <span className={styles.meta}>
              {items.length} {items.length === 1 ? 'session' : 'sessioner'} · {formatBytes(totalBytes)}
            </span>
            <progress className={styles.bar} max={1} value={overall} aria-label="Samlet fremdrift" />
          </div>
          <ol className={styles.list}>
            {picked.map(({ item }, index) => (
              <SessionRow
                key={item.key}
                index={index}
                item={item}
                job={item.state.kind === 'importing' ? jobs[item.state.jobId] : null}
                running={running}
                onRemove={() => setPicked((list) => list.filter((p) => p.item.key !== item.key))}
                onRetry={() => {
                  setState(item.key, { kind: 'queued' });
                  void run(item.key);
                }}
              />
            ))}
          </ol>
        </>
      )}

      <Checkbox
        label="Foreslå løkker mellem sessionerne og løs én gang"
        hint="kommer"
        disabled
        title="Kræver ruxd-64g's placering af sessioner (XFeat + kameranærhed) — ikke i API'et endnu."
      />
    </FormDialog>
  );
}

function SessionRow({
  index,
  item,
  job,
  running,
  onRemove,
  onRetry,
}: {
  index: number;
  item: SessionItem;
  job: Job | null | undefined;
  running: boolean;
  onRemove: () => void;
  onRetry: () => void;
}) {
  const s = item.state;
  const fraction = sessionFraction(s, job);
  let status: string;
  let tone: 'muted' | 'good' | 'warn' | 'crit' = 'muted';
  switch (s.kind) {
    case 'queued':
      status = 'Venter';
      break;
    case 'invalid':
      status = s.reason;
      tone = 'crit';
      break;
    case 'uploading': {
      const pct = s.total > 0 ? Math.round((100 * s.sent) / s.total) : 0;
      status = s.notice ?? `Uploader ${pct} % · ${formatEta(etaSeconds(s.sent, s.total, Date.now() - s.startedAt))}`.replace(/ · $/, '');
      break;
    }
    case 'starting':
      status = 'Starter import…';
      break;
    case 'importing':
      status = job?.progress?.stage_label ? `Importerer: ${job.progress.stage_label} ${Math.round(100 * (job.progress.fraction ?? 0))} %` : 'Importerer…';
      break;
    case 'done':
      status = s.summary;
      tone = 'good';
      break;
    case 'duplicate':
      status = s.message;
      tone = 'warn';
      break;
    case 'failed':
      status = s.message;
      tone = 'crit';
      break;
    case 'cancelled':
      status = 'Annulleret';
      break;
  }
  return (
    <li className={styles.row}>
      <span className={styles.ordinal} aria-hidden="true">
        {index + 1}
      </span>
      <div className={styles.rowBody}>
        <div className={styles.rowHead}>
          <span className={styles.name}>{item.name}</span>
          <span className={styles.meta}>{formatBytes(item.size)}</span>
        </div>
        {(isBusy(s) || s.kind === 'done') && <progress className={styles.bar} max={1} value={fraction} aria-label={`Fremdrift for ${item.name}`} />}
        <span className={`${styles.status} ${styles[tone]}`} role={s.kind === 'failed' ? 'alert' : undefined}>
          {status}
        </span>
      </div>
      {!running && (s.kind === 'failed' || s.kind === 'cancelled') && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Prøv igen
        </Button>
      )}
      {!running && (s.kind === 'queued' || s.kind === 'invalid') && (
        <Button variant="outline" size="sm" onClick={onRemove} aria-label={`Fjern ${item.name}`}>
          Fjern
        </Button>
      )}
    </li>
  );
}
